import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { GridRow } from 'dibs-elements/exports/Grid';
import HpRespPromoModulePromoItem from './HpRespPromoModulePromoItem';
import { HpRespModuleContainer } from '../HpRespModuleContainer/HpRespModuleContainer';
import { trackPromoImpressions, trackPromoClick } from './helpers/PromoTracking';
import { trackModuleLocation } from '../helpers/moduleLocationTracking';
import { SharedInViewport } from '../../sharedComponents/SharedInViewport';

import { HpRespPromoModulePrimary_componentModule } from './__generated__/HpRespPromoModulePrimary_componentModule.graphql';

type Props = {
    componentModule: HpRespPromoModulePrimary_componentModule;
    useLazyLoadImages: boolean;
    isMobile: boolean;
    moduleIndex: number;
    totalModules: number;
};

const HpRespPromoModulePrimary: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    isMobile,
    moduleIndex,
    totalModules,
}) => {
    if (!componentModule) {
        return null;
    }

    const { messages } = componentModule;
    const { title } = messages || {};
    const items = (messages?.items || []).filter(filterNulls);
    const isSinglePromo = items.length === 1;

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (componentModule && inViewport) {
            trackPromoImpressions({ items, startingIndex: 0 });
        }
    };

    return (
        <HpRespModuleContainer addTopGap={!title}>
            <HpRespModuleContainer.Header title={title} />
            <SharedInViewport stopWhenInViewport onInViewportChange={handleInitialDisplay}>
                <GridRow>
                    {items.map((promotion, index) => (
                        <HpRespPromoModulePromoItem
                            key={promotion?.cmsId || index}
                            index={index}
                            promotion={promotion}
                            useLazyLoadImages={useLazyLoadImages}
                            onClick={() => {
                                trackPromoClick({ item: promotion, index });
                                trackModuleLocation({
                                    label: `promo banner #${index + 1}`,
                                    moduleIndex,
                                    totalModules,
                                });
                            }}
                            isSinglePromo={isSinglePromo}
                            isMobile={isMobile}
                        />
                    ))}
                </GridRow>
            </SharedInViewport>
        </HpRespModuleContainer>
    );
};

export default createFragmentContainer(HpRespPromoModulePrimary, {
    componentModule: graphql`
        fragment HpRespPromoModulePrimary_componentModule on PromoModule {
            messages(userType: $userType) {
                title
                items {
                    cmsId
                    linkData {
                        # path is used in PromoTrackingOld.js
                        path
                    }
                    ...HpRespPromoModulePromoItem_promotion
                }
            }
        }
    `,
});
